<template>
  <teleport to="body">
    <div v-if="open" class="fixed inset-0 z-[1000] overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="$emit('close')"></div>

      <!-- Modal -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div
          class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/50 w-full max-w-md transform transition-all"
          @click.stop
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700"
          >
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Opnieuw beoordelen</h2>
            <button
              @click="$emit('close')"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="px-6 py-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Je staat op het punt om
              <span class="font-semibold text-gray-900 dark:text-white">{{
                prospect?.organization_name
              }}</span>
              opnieuw ter beoordeling in te dienen.
            </p>

            <!-- Warning box -->
            <div
              class="flex items-start gap-3 p-3 mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
            >
              <ExclamationTriangleIcon
                class="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
              />
              <div class="text-sm text-amber-800 dark:text-amber-300">
                <p class="font-medium">Let op: deze actie kan niet ongedaan worden gemaakt.</p>
                <ul class="mt-1 list-disc list-inside text-amber-700 dark:text-amber-400">
                  <li>Alle toewijzingen worden vrijgegeven</li>
                  <li>Openstaande acties worden geannuleerd</li>
                </ul>
              </div>
            </div>

            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Reden voor herbeoordeling <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="reason"
              rows="4"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
              placeholder="Beschrijf waarom dit project opnieuw beoordeeld moet worden..."
            ></textarea>

            <!-- Error message -->
            <p v-if="error" class="mt-2 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700"
          >
            <button
              @click="$emit('close')"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Annuleren
            </button>
            <button
              @click="handleSubmit"
              :disabled="!reason.trim() || isSubmitting"
              class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {{ isSubmitting ? 'Verwerken...' : 'Opnieuw beoordelen' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  prospect: {
    type: Object,
    default: null
  },
  isSubmitting: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close', 'submit'])

const reason = ref('')

// Reset reason when modal opens
watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      reason.value = ''
    }
  }
)

const handleSubmit = () => {
  if (reason.value.trim()) {
    emit('submit', reason.value.trim())
  }
}
</script>
